import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { api, BACKEND_URL } from '../api';
import { toast } from 'sonner';
import {
  UtensilsCrossed, ShoppingBag, Landmark, Compass, Building2, Wrench, Music,
  Car, Plus, Star, MapPin, ExternalLink, Search, Filter,
  Check, Clock, Trash2, ChevronDown, ChevronUp, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';

const API_BASE = BACKEND_URL;

const CATEGORY_ICONS = {
  "Restaurants & Cafés": UtensilsCrossed,
  "Shops & Markets": ShoppingBag,
  "Landmarks & History": Landmark,
  "Excursions & Tours": Compass,
  "Hotels & Lodging": Building2,
  "Services": Wrench,
  "Nightlife & Entertainment": Music,
  "Transportation": Car,
};

const CATEGORY_COLORS = {
  "Restaurants & Cafés": "bg-orange-50 text-orange-700 border-orange-200",
  "Shops & Markets": "bg-purple-50 text-purple-700 border-purple-200",
  "Landmarks & History": "bg-amber-50 text-amber-700 border-amber-200",
  "Excursions & Tours": "bg-green-50 text-green-700 border-green-200",
  "Hotels & Lodging": "bg-blue-50 text-blue-700 border-blue-200",
  "Services": "bg-slate-50 text-slate-700 border-slate-200",
  "Nightlife & Entertainment": "bg-pink-50 text-pink-700 border-pink-200",
  "Transportation": "bg-cyan-50 text-cyan-700 border-cyan-200",
};

const StarRating = ({ rating, onRate, size = 'md', interactive = false }) => {
  const [hover, setHover] = useState(0);
  const sz = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className="flex items-center gap-0.5" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(star => {
        const val = hover || rating;
        const full = val >= star;
        const half = val >= star - 0.5 && val < star;
        return (
          <button
            key={star}
            type="button"
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'} relative`}
            onMouseMove={(e) => {
              if (!interactive) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const isLeftHalf = e.clientX < rect.left + rect.width / 2;
              setHover(isLeftHalf ? star - 0.5 : star);
            }}
            onClick={(e) => {
              if (!interactive) return;
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              const isLeftHalf = e.clientX < rect.left + rect.width / 2;
              onRate?.(isLeftHalf ? star - 0.5 : star);
            }}
          >
            <Star className={`${sz} ${full ? 'fill-amber-400 text-amber-400' : half ? 'fill-amber-400/50 text-amber-400' : 'text-slate-300'}`} />
          </button>
        );
      })}
    </div>
  );
};

const ListingCard = ({ listing, onRate, isAdmin, onApprove, onDelete, expanded, onToggle }) => {
  const Icon = CATEGORY_ICONS[listing.category] || Landmark;
  const colorClass = CATEGORY_COLORS[listing.category] || "bg-slate-50 text-slate-600 border-slate-200";
  const hasPhotos = listing.photo_count > 0;

  return (
    <Card className={`overflow-hidden transition-shadow hover:shadow-md ${listing.status === 'pending' ? 'border-amber-200 bg-amber-50/20' : ''}`}>
      <div className="flex flex-col sm:flex-row">
        {/* Photo */}
        {hasPhotos && (
          <div className="sm:w-40 h-32 sm:h-auto flex-shrink-0 bg-slate-100 overflow-hidden">
            <img
              src={`${API_BASE}/api/food-fun/${listing.listing_id}/photos/0`}
              alt={listing.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <CardContent className={`flex-1 py-4 ${hasPhotos ? '' : 'px-5'}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold text-slate-800">{listing.name}</h3>
                {listing.status === 'pending' && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Pending Review</Badge>}
              </div>
              <Badge variant="outline" className={`text-xs mt-1 ${colorClass}`}>
                <Icon className="w-3 h-3 mr-1" />{listing.category}
              </Badge>
            </div>
            
            {/* Rating */}
            <div className="text-right flex-shrink-0">
              <StarRating rating={listing.avg_rating || 0} size="sm" />
              <p className="text-xs text-slate-400 mt-0.5">{listing.rating_count || 0} rating{listing.rating_count !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {listing.description && (
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{listing.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
            {listing.maps_link && (
              <a href={listing.maps_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                <MapPin className="w-3.5 h-3.5" />Map
              </a>
            )}
            {listing.website && (
              <a href={listing.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                <ExternalLink className="w-3.5 h-3.5" />Website
              </a>
            )}
            {listing.address && (
              <span className="text-slate-400 text-xs">{listing.address}</span>
            )}
          </div>

          {/* Expandable: rate + admin actions */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100">
            <button onClick={onToggle} className="text-xs text-slate-400 hover:text-primary flex items-center gap-1">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Less' : 'Rate & Details'}
            </button>
            <span className="text-xs text-slate-300">·</span>
            <span className="text-xs text-slate-400">by {listing.submitted_by_name}</span>
          </div>

          {expanded && (
            <div className="mt-3 space-y-3">
              {/* Rate this place */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">Your rating:</span>
                <StarRating rating={listing.your_rating || 0} interactive onRate={(r) => onRate(listing.listing_id, r)} />
              </div>

              {/* More photos */}
              {listing.photo_count > 1 && (
                <div className="flex gap-2">
                  {Array.from({ length: listing.photo_count }, (_, i) => (
                    <img
                      key={i}
                      src={`${API_BASE}/api/food-fun/${listing.listing_id}/photos/${i}`}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}

              {/* Admin actions */}
              {isAdmin && (
                <div className="flex gap-2">
                  {listing.status === 'pending' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => onApprove(listing.listing_id)}>
                      <Check className="w-3 h-3 mr-1" />Approve
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => onDelete(listing.listing_id)}>
                    <Trash2 className="w-3 h-3 mr-1" />Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
};

const FoodFunPage = () => {
  const { user, isAdmin } = useAuth();
  const [listings, setListings] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', category: '', description: '', address: '', maps_link: '', website: '', rating: 0 });
  const [addPhotos, setAddPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [listingsRes, catsRes] = await Promise.all([
        api.get('/food-fun'),
        api.get('/food-fun/categories'),
      ]);
      setListings(listingsRes.data || []);
      setCategories(catsRes.data || []);
      
      if (isAdmin) {
        const pendingRes = await api.get('/food-fun-pending');
        setPendingListings(pendingRes.data || []);
      }
    } catch {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRate = async (listingId, rating) => {
    try {
      const res = await api.post(`/food-fun/${listingId}/rate`, { rating });
      setListings(prev => prev.map(l => l.listing_id === listingId
        ? { ...l, avg_rating: res.data.avg_rating, rating_count: res.data.rating_count, your_rating: res.data.your_rating }
        : l
      ));
      toast.success(`Rated ${rating} stars`);
    } catch { toast.error('Failed to rate'); }
  };

  const handleApprove = async (listingId) => {
    try {
      await api.put(`/food-fun/${listingId}`, { status: 'approved' });
      toast.success('Listing approved');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await api.delete(`/food-fun/${listingId}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleSubmit = async () => {
    if (!addForm.name || !addForm.category) {
      toast.error('Name and category are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/food-fun', addForm);
      const newId = res.data.listing_id;
      
      // Upload photos
      for (const photo of addPhotos) {
        const formData = new FormData();
        formData.append('file', photo);
        await api.post(`/food-fun/${newId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      
      // Submit initial rating if provided
      if (addForm.rating > 0) {
        await api.post(`/food-fun/${newId}/rate`, { rating: addForm.rating });
      }
      
      toast.success(isAdmin ? 'Listing added!' : 'Submitted for review!');
      setShowAdd(false);
      setAddForm({ name: '', category: '', description: '', address: '', maps_link: '', website: '', rating: 0 });
      setAddPhotos([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter listings
  const filtered = listings.filter(l => {
    if (categoryFilter !== 'all' && l.category !== categoryFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      return l.name.toLowerCase().includes(term) || l.description?.toLowerCase().includes(term) || l.address?.toLowerCase().includes(term);
    }
    return true;
  });

  const addBtn = (
    <Button className="rounded-full bg-rojo hover:bg-rojo-dark h-9" onClick={() => setShowAdd(true)} data-testid="add-listing-btn">
      <Plus className="w-4 h-4 mr-1" />Recommend
    </Button>
  );

  return (
    <PageLayout title="Local Guide" headerSub="Community-curated guide to Antigua" headerActions={addBtn} maxWidth="max-w-4xl">
      <div className="space-y-5">
        {/* Pending banner for admin */}
        {isAdmin && pendingListings.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">{pendingListings.length} pending submission{pendingListings.length !== 1 ? 's' : ''} to review</span>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-200 text-amber-700"
                onClick={() => setCategoryFilter('pending')}>
                Review
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search places..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="food-fun-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-52 h-9" data-testid="food-fun-category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {isAdmin && pendingListings.length > 0 && (
                <SelectItem value="pending">Pending Review ({pendingListings.length})</SelectItem>
              )}
              {categories.map(c => {
                const Icon = CATEGORY_ICONS[c] || Landmark;
                return <SelectItem key={c} value={c}>{c}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => {
            const Icon = CATEGORY_ICONS[cat] || Landmark;
            const count = listings.filter(l => l.category === cat).length;
            if (count === 0) return null;
            return (
              <button
                key={cat}
                onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  categoryFilter === cat ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />{cat.split(' ')[0]} <span className="text-xs opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Listings */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (categoryFilter === 'pending' ? pendingListings : filtered).length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Compass className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">
                {search ? 'No places match your search' : categoryFilter === 'pending' ? 'No pending submissions' : 'No places added yet'}
              </p>
              <p className="text-sm text-slate-400 mt-1">Know a great spot? Click "Recommend a Place" to add it!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {(categoryFilter === 'pending' ? pendingListings : filtered).map(listing => (
              <ListingCard
                key={listing.listing_id}
                listing={listing}
                onRate={handleRate}
                isAdmin={isAdmin}
                onApprove={handleApprove}
                onDelete={handleDelete}
                expanded={expandedId === listing.listing_id}
                onToggle={() => setExpandedId(expandedId === listing.listing_id ? null : listing.listing_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Listing Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Recommend a Place</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="e.g., Café Condesa" data-testid="listing-name" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger data-testid="listing-category"><SelectValue placeholder="Select category..." /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="Why do you recommend this place?" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Address</Label>
                <Input value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} placeholder="5a Avenida Norte" />
              </div>
              <div>
                <Label>Google Maps Link</Label>
                <Input value={addForm.maps_link} onChange={(e) => setAddForm({ ...addForm, maps_link: e.target.value })} placeholder="https://maps.google.com/..." />
              </div>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={addForm.website} onChange={(e) => setAddForm({ ...addForm, website: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>Your Rating</Label>
              <div className="mt-1">
                <StarRating rating={addForm.rating} interactive onRate={(r) => setAddForm({ ...addForm, rating: r })} />
              </div>
            </div>
            <div>
              <Label>Photos (up to 3)</Label>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setAddPhotos(Array.from(e.target.files || []).slice(0, 3))}
                className="text-sm"
              />
              {addPhotos.length > 0 && <p className="text-xs text-slate-400 mt-1">{addPhotos.length} photo{addPhotos.length !== 1 ? 's' : ''} selected</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-rojo hover:bg-rojo-dark" onClick={handleSubmit} disabled={submitting || !addForm.name || !addForm.category} data-testid="submit-listing-btn">
              {submitting ? 'Submitting...' : isAdmin ? 'Add to Directory' : 'Submit for Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default FoodFunPage;
